import React, { ReactElement, useCallback, useState, useEffect, useMemo, useRef } from "react";
import {
    Button,
    makeStyles,
    TextField,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    IconButton,
} from "@material-ui/core";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import ActionDialog from "../../common/ActionDialog";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import theme from "../../../themes";
import { PhoneNumberFormatCustom } from "../../common/utilities/PhoneNumberFormat";
import { useEnumStrings } from "../../enumStrings";
import EditOrangeIcon from "../../../images/edit-icon-orange.png";
import UploadRoundIcon from "../../../images/upload-rounded.png";
import TrashIcon from "../../../images/trash.png";
import PlayVideoIcon from "../../../images/play-video-icon.svg";
import BannerUploadModal from "../ChamberAdmin/BannerUploadModal";
import BannerPreviewModal from "../ChamberAdmin/BannerPreviewModal";
import DeleteBannerModal from "../ChamberAdmin/DeleteBannerModal";
import {
    useUpdateChamberMemberMutation,
    useGetChamberMemberQuery,
    useGetUserQuery,
    useChamberMemberChangeAdminConsoleLoginEmailMutation,
    BannerContentTypeEnum,
} from "../../../graphql/types";
import { getIn, useFormik } from "formik";
import * as yup from "yup";
import { useLocation } from "react-router-dom";
import Categories from "../../common/utilities/Categories";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import useGetChamberMemberFromUserIdInAuth from "../../common/Hooks/useGetChamberMemberFromUserIdInAuth";
import _without from "lodash/without";
import LogoPicker from "../../common/utilities/LogoPicker";
import ProvincePicker from "../../common/utilities/ProvincePicker";
import { getInitialStateOfCustomContacts } from "../../common/utilities/MultiContactForm";
import { toast, ToastContainer } from "react-toastify";
import IdaChatbotIcon from "../../../images/ida-chatbot-icon.png";
import IDAGenerateModal from "../../common/IDAGenerateModal";
import IDAOverwriteDialog from "../../common/IDAOverwriteDialog";
import SuccessDialog from "../../common/SuccessDialog";
import MingcuteQuestionIcon from "../../../images/mingcute_question-line.png";
import { useHandleLogout } from "../../common/Hooks/useHandleLogout";
import { OWN_LOGIN_EMAIL_REFUSAL } from "../../../apolloErrorReporting";
import UnsavedChangesPrompt from "../../common/UnsavedChangesPrompt";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ExportButton from "../../common/ExportButton";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { openMemberInstructionPdf } from "../../common/openInstructionPdf";
import FeaturedMemberBannerGuidance from "../../common/FeaturedMemberBannerGuidance";
import CmpCompanyContactCards from "./CmpContacts/CmpCompanyContactCards";
import useMemberDashboardAccess from "../../common/Hooks/useMemberDashboardAccess";
import { buildCmpContactExtensionInput, EMPTY_CMP_CONTACT_FORM_VALUES, getCmpContactFormValues } from "./CmpContacts/cmpContactCards";

const CHARACTER_LIMIT_DESCRIPTION = 2000;

const useStyles = makeStyles((themes: any) => ({
    root: {
        backgroundColor: theme.palette.primary.main,
        minHeight: "100vh",
        paddingTop: "10px",
        paddingBottom: "80px",
        overflowX: "hidden" as const,
    },
    titleContent: {
        display: "flex",
        flexDirection: "column-reverse",
        [theme.breakpoints.up("lg")]: {
            paddingTop: "15px",
        },
        "@media (max-width: 768px)": {
            minHeight: "auto",
        },
    },
    titleRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "15px 40px",
        [theme.breakpoints.down("sm")]: {
            padding: "12px 16px",
        },
    },
    title: {
        fontSize: "32px",
        color: "white",
        fontWeight: "bold",
        display: "flex",
        flexDirection: "column",
        [theme.breakpoints.down("sm")]: {
            fontSize: "22px",
        },
    },
    subtitle: {
        marginLeft: "1px",
        fontWeight: "normal",
        fontSize: "18px",
        color: "white",
        [theme.breakpoints.down("sm")]: {
            fontSize: "16px",
            lineHeight: "1.55",
        },
    },
    pageHeaderButtonRow: {
        display: "flex",
        gap: "12px",
        alignItems: "center",
        flexWrap: "wrap" as const,
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column" as const,
            alignItems: "stretch",
            gap: "8px",
            width: "100%",
            "& > div": { width: "100%" },
            "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
                width: "100%",
                marginTop: 0,
            },
        },
    },
    content: {
        background: "#FFFFFF",
        flexGrow: 1,
        borderRadius: "16px",
        margin: "10px 20px 20px",
        minHeight: "85vh",
        overflowX: "hidden" as const,
        maxWidth: "100%",
        [theme.breakpoints.down("sm")]: {
            margin: 0,
        },
        flex: "1",
        marginTop: "33px",
        paddingTop: "30px",
        paddingBottom: "33px",
    },
    sectionHeader: {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "30px",
        padding: "12px 32px",
        display: "inline-block",
        position: "relative" as const,
        top: "-24px",
        left: "0",
        marginBottom: "-16px",
        [theme.breakpoints.down("sm")]: {
            padding: "8px 18px",
            top: "-20px",
            maxWidth: "calc(100% - 8px)",
        },
    },
    sectionTitle: {
        color: "#FFFFFF",
        fontSize: "20px",
        fontWeight: 700,
        margin: 0,
        textAlign: "center" as const,
        lineHeight: "24px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "14px",
            lineHeight: "18px",
        },
    },
    sectionHelperText: {
        color: "#000000",
        fontSize: "16px",
        lineHeight: "18px",
        fontWeight: 400,
        marginBottom: "10px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "14px",
            lineHeight: "20px",
        },
    },
    formSection: {
        maxWidth: "1096px",
        margin: "40px auto 40px auto",
        padding: "0px 30px 30px 30px",
        border: "1px solid #9E9E9E",
        borderRadius: "16px",
        position: "relative" as const,
        boxSizing: "border-box" as const,
        overflow: "visible",
        backgroundColor: "#FFFFFF",
        [theme.breakpoints.down("sm")]: {
            margin: "40px 12px 24px 12px",
            padding: "0 16px 20px 16px",
            maxWidth: "calc(100% - 24px)",
        },
    },
    companyInfoGrid: {
        display: "grid",
        gridTemplateColumns: "409fr 537fr",
        gap: "20px 20px",
        gridTemplateAreas: `
            "companyName description"
            "companyWebsite description"
            "contactName phone"
            "contactEmail googleReview"
            "logo categories"
        `,
        alignItems: "start",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
            gridTemplateAreas: `
                "companyName"
                "description"
                "companyWebsite"
                "contactName"
                "phone"
                "contactEmail"
                "googleReview"
                "logo"
                "categories"
            `,
        },
    },
    formRow: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "20px",
        marginBottom: "20px",
        alignItems: "start",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
        },
    },
    formRowSingle: {
        display: "grid",
        gridTemplateColumns: "1fr",
        marginBottom: "20px",
    },
    fieldLabel: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        width: "100%",
        fontSize: "18px",
        color: "#000000",
        fontWeight: 600,
        marginBottom: "8px",
    },
    requiredAsterisk: {
        color: "red",
    },
    descriptionHelperText: {
        fontFamily: "Inter, sans-serif",
        fontWeight: 400,
        fontSize: "16px",
        color: "#24272C",
        lineHeight: "22px",
        marginBottom: "4px",
    },
    logoHelperText: {
        fontSize: "16px",
        color: theme.palette.text.primary,
        marginBottom: "12px",
        lineHeight: "18px",
    },
    categoryHelperText: {
        fontSize: "16px",
        color: theme.palette.text.primary,
        marginBottom: "12px",
        lineHeight: "18px",
    },
    companyInput: {
        paddingBottom: "10px",
        borderRadius: "8px",
        width: "100%",
        backgroundColor: "#FFFFFF",
        "& .MuiOutlinedInput-root": {
            backgroundColor: "#FFFFFF",
            "& fieldset": {
                borderColor: theme.palette.text.primary,
                borderRadius: "8px",
            },
            "&:hover fieldset": {
                borderColor: theme.palette.text.primary,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.text.primary,
            },
        },
        "& label.Mui-focused": {
            color: "#000000",
        },
        "& .MuiInputBase-input": {
            fontSize: "16px",
            color: "#333333",
            "&::placeholder": {
                color: "#333333",
                opacity: 1,
            },
        },
    },
    upgradeBanner: {
        backgroundColor: theme.palette.primary.main,
        color: "white",
        borderRadius: "10px",
        padding: "12px 24px",
        textAlign: "center" as const,
        fontSize: "16px",
        fontWeight: "bold",
        cursor: "pointer",
        margin: "20px auto",
        maxWidth: "800px",
        display: "block",
        width: "fit-content",
        "&:hover": {
            backgroundColor: "#D4A353",
        },
    },
    addBannerChip: {
        backgroundColor: "#22C55E",
        color: "white",
        borderRadius: "10px",
        padding: "12px 24px",
        textAlign: "center" as const,
        fontSize: "16px",
        fontWeight: "bold",
        cursor: "pointer",
        margin: "10px auto",
        maxWidth: "800px",
        display: "block",
        width: "fit-content",
        "&:hover": {
            backgroundColor: "#16A34A",
        },
    },
    updateButton: {
        backgroundColor: theme.palette.primary.main,
        color: "white",
        fontSize: "16px",
        fontWeight: "bold",
        borderRadius: "10px",
        padding: "15px 40px",
        textTransform: "none" as const,
        minWidth: "350px",
        marginTop: "20px",
        "&:hover": {
            backgroundColor: "#D4A353",
        },
        [theme.breakpoints.down("sm")]: {
            fontSize: "16px",
            padding: "14px 32px",
            minWidth: 0,
            width: "100%",
            maxWidth: "320px",
        },
    },
    logoContainer: {
        display: "flex",
        flexDirection: "column" as const,
        gap: "10px",
    },
    logoHelper: {
        fontSize: "16px",
        lineHeight: "18px",
        color: "#24272C",
        margin: "0 0 8px 0",
    },
    invalidError: {
        textAlign: "center" as const,
        color: "red",
        marginTop: "10px",
    },
    importantWarning: {
        margin: "20px 0 10px 0",
        padding: "0",
    },
    importantTitle: {
        fontSize: "18px",
        fontWeight: 700,
        color: "#000000",
        marginBottom: "8px",
        fontFamily: "DM Sans, sans-serif",
        lineHeight: "28px",
    },
    importantDesc: {
        fontSize: "16px",
        fontWeight: 400,
        color: "#000000",
        lineHeight: "28px",
        fontFamily: "DM Sans, sans-serif",
        "& u": {
            color: theme.palette.primary.main,
        },
    },
    hideAddressCheckbox: {
        marginLeft: "0px",
        marginBottom: "10px",
        "& .MuiTypography-root": {
            fontSize: "16px",
            lineHeight: "18px",
            color: "#24272C",
            fontFamily: "DM Sans, sans-serif",
        },
    },
    sectionHeaderGreen: {
        backgroundColor: theme.palette.success?.main || "#4CAF50",
        borderRadius: "30px",
        padding: "12px 32px",
        display: "inline-block",
        position: "relative" as const,
        top: "-24px",
        left: "0",
        [theme.breakpoints.down("sm")]: {
            padding: "8px 18px",
            top: "-20px",
            maxWidth: "calc(100% - 8px)",
        },
    },
    sectionHeaderBlack: {
        backgroundColor: "#000000",
        borderRadius: "30px",
        padding: "12px 32px",
        display: "inline-block",
        position: "relative" as const,
        top: "-24px",
        left: "0",
        [theme.breakpoints.down("sm")]: {
            padding: "8px 18px",
            top: "-20px",
            maxWidth: "calc(100% - 8px)",
        },
    },
    bannersGrid: {
        display: "flex",
        flexDirection: "row" as const,
        alignItems: "center",
        gap: "20px",
        marginTop: "20px",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px 12px",
            alignItems: "stretch",
            justifyContent: "stretch",
            width: "100%",
        },
    },
    bannerContainer: {
        display: "flex",
        width: "209px",
        flexDirection: "column" as const,
        alignItems: "center",
        gap: "16px",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            gap: "8px",
            minWidth: 0,
        },
    },
    bannerLabel: {
        textAlign: "center" as const,
        fontWeight: 600,
        color: theme.palette.primary.main,
        fontSize: "20px",
        lineHeight: "24px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "15px",
        },
    },
    bannerCard: {
        backgroundColor: "#FFFFFF",
        border: `2px solid ${theme.palette.primary.main}`,
        borderRadius: "10px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column" as const,
        width: "209px",
        height: "175px",
        boxSizing: "border-box" as const,
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            height: "175px",
        },
    },
    bannerCardEmpty: {
        backgroundColor: "#FFFFFF",
        border: `2px solid ${theme.palette.primary.main}`,
        borderRadius: "10px",
        display: "flex",
        flexDirection: "column" as const,
        justifyContent: "center",
        alignItems: "center",
        width: "182px",
        height: "114px",
        boxSizing: "border-box" as const,
        cursor: "pointer",
        padding: "22px 12px",
        gap: "10px",
        "&:hover": {
            backgroundColor: "#FFF3D6",
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            height: "90px",
            padding: "12px 8px",
            gap: "8px",
        },
    },
    bannerSlotDisabled: {
        display: "flex",
        flexDirection: "column" as const,
        justifyContent: "center",
        alignItems: "center",
        padding: "22px 12px",
        gap: "10px",
        width: "182px",
        height: "114px",
        backgroundColor: "#9E9E9E",
        borderRadius: "10px",
        boxSizing: "border-box" as const,
        cursor: "not-allowed",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            height: "90px",
            padding: "12px 8px",
            gap: "8px",
        },
    },
    bannerSlotDisabledIcon: {
        width: "36px",
        height: "36px",
        filter: "brightness(0) invert(1)",
        [theme.breakpoints.down("sm")]: {
            width: "28px",
            height: "28px",
        },
    },
    bannerSlotDisabledText: {
        fontFamily: "DM Sans, sans-serif",
        fontWeight: 600,
        fontSize: "20px",
        lineHeight: "24px",
        textAlign: "center" as const,
        color: "#FFFFFF",
        [theme.breakpoints.down("sm")]: {
            fontSize: "12px",
            lineHeight: "1.3",
        },
    },
    bannerImageContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        position: "relative" as const,
        padding: "12px",
        flex: 1,
    },
    bannerImage: {
        width: "100%",
        height: "100%",
        objectFit: "cover" as const,
        borderRadius: "4px",
    },
    videoPlaceholder: {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#FFFFFF",
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: "4px",
        color: theme.palette.primary.main,
        fontFamily: "DM Sans, sans-serif",
        gap: "4px",
    },
    bannerActions: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 12px",
        backgroundColor: "transparent",
    },
    editDeleteButtons: {
        display: "flex",
        gap: "8px",
    },
    editButton: {
        backgroundColor: "#E3E3E3",
        borderRadius: "5px",
        width: "36px",
        height: "33px",
        minWidth: "36px",
        padding: "4px",
        "&:hover": { backgroundColor: "#D0D0D0" },
    },
    trashButton: {
        backgroundColor: "#ED1C24",
        borderRadius: "5px",
        width: "36px",
        height: "33px",
        minWidth: "36px",
        padding: "4px",
        "&:hover": { backgroundColor: "#D41920" },
    },
    arrowButtons: {
        display: "flex",
        gap: "4px",
    },
    arrowButton: {
        backgroundColor: theme.palette.primary.main,
        color: "white",
        padding: "4px",
        borderRadius: "5px",
        minWidth: "29px",
        width: "29px",
        height: "26px",
        "&:hover": { backgroundColor: "#D4A353" },
        "&.Mui-disabled": { backgroundColor: "#D9D9D9", color: "#666" },
    },
    arrowIconSmall: {
        fontSize: "12px",
    },
    arrowIconLeft: {
        fontSize: "12px",
        marginLeft: "4px",
    },
    iconSize36: {
        width: "36px",
        height: "36px",
        [theme.breakpoints.down("sm")]: {
            width: "28px",
            height: "28px",
        },
    },
    iconSize20: {
        width: "20px",
        height: "20px",
    },
    iconInverted: {
        filter: "brightness(0) invert(1)",
    },
    uploadBannerText: {
        color: theme.palette.primary.main,
        fontSize: "20px",
        fontWeight: 600,
        margin: 0,
        lineHeight: "24px",
        textAlign: "center" as const,
        [theme.breakpoints.down("sm")]: {
            fontSize: "12px",
            lineHeight: "1.3",
        },
    },
    videoWatchText: {
        fontSize: "14px",
        fontWeight: 600,
        color: theme.palette.primary.main,
    },
    bannerInfoText: {
        fontSize: "16px",
        lineHeight: "24px",
        color: "#000000",
        textAlign: "center" as const,
        marginBottom: "16px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "14px",
            lineHeight: "20px",
        },
    },
    bannerInfoTextLarge: {
        fontSize: "20px",
        lineHeight: "24px",
        color: "#666666",
        textAlign: "center" as const,
        marginBottom: "16px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "15px",
            lineHeight: "20px",
        },
    },
    bannerRecommendationText: {
        fontSize: "16px",
        lineHeight: "24px",
        color: "#000000",
        textAlign: "center" as const,
        marginBottom: "24px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "14px",
            lineHeight: "20px",
        },
    },
    boldText: {
        fontWeight: 700,
    },
    centerFlexContainer: {
        display: "flex",
        justifyContent: "center",
    },
    previewBannersButton: {
        backgroundColor: "#24272C",
        color: "#FFFFFF",
        borderRadius: "8px",
        padding: "14px 40px",
        fontSize: "16px",
        fontWeight: 700,
        fontFamily: "DM Sans, sans-serif",
        textTransform: "none" as const,
        "&:hover": { backgroundColor: "#6B6B6B" },
        "&.Mui-disabled": {
            backgroundColor: "#888888",
            color: "#FFFFFF",
            opacity: 1,
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            padding: "14px 24px",
            fontSize: "15px",
        },
    },
    clickHereLink: {
        color: theme.palette.primary.main,
        textDecoration: "underline",
        cursor: "pointer",
        fontWeight: 700,
    },
    idaHelperContainer: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: "10px",
        padding: "2px 5px",
        marginTop: "8px",
        backgroundColor: "#FFFFFF",
    },
    idaIcon: {
        width: "33px",
        height: "26px",
        objectFit: "contain" as const,
    },
    idaHelperText: {
        fontWeight: 400,
        fontSize: "12px",
        color: theme.palette.primary.main,
        lineHeight: "15px",
        "& b": {
            fontWeight: 700,
        },
    },
    idaGenerateButton: {
        backgroundColor: "#4CAF50",
        border: "1px solid #4CAF50",
        borderRadius: "10px",
        padding: "1px 13px",
        height: "17px",
        minWidth: "71px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        "&:hover": {
            backgroundColor: "#388E3C",
        },
    },
    idaGenerateButtonText: {
        fontWeight: 400,
        fontSize: "10px",
        color: "#FFFFFF",
        lineHeight: "15px",
    },
}));

const ManageCompanyProfile = (): ReactElement => {
    const classes = useStyles();
    const enumStrings = useEnumStrings();
    const location = useLocation();
    const chamberMemberFromAuth = useGetChamberMemberFromUserIdInAuth();
    const chamberInfo = useGetChamberFromUserIdInAuth();
    const handleLogout = useHandleLogout();

    const chamberMemberId = useMemo(() => {
        const stateId = (
            (location.state || { chamberMemberId: undefined }) as {
                chamberMemberId: string | undefined;
            }
        ).chamberMemberId;

        if (stateId) return stateId;

        return chamberMemberFromAuth?.id;
    }, [location.state, chamberMemberFromAuth]);

    const [loader, setLoader] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [categoryDisable, setCategoryDisable] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [updateSuccessDialogOpen, setUpdateSuccessDialogOpen] = useState(false);
    const [adminContactChanged, setAdminContactChanged] = useState(false);
    const savedValuesRef = useRef<string>("");
    const originalAdminContactRef = useRef({
        adminConsoleContactName: "",
        cpaAdminCompanyAdminContactTitle: "",
        adminConsoleLoginEmail: "",
        cpaAdminCompanyAdminContactNumber: "",
        cpaAdminCompanyAdminContactNumberExtension: "",
    });

    // Track whether user has directly interacted with admin fields (stops auto-sync for that field)
    const adminFieldsTouched = useRef({
        adminConsoleContactName: false,
        cpaAdminCompanyAdminContactTitle: false,
        adminConsoleLoginEmail: false,
        cpaAdminCompanyAdminContactNumber: false,
        cpaAdminCompanyAdminContactNumberExtension: false,
    });

    // Track whether primary and admin fields initially had the same value (linked state)
    const fieldsInitiallyLinked = useRef({
        adminConsoleContactName: false,
        cpaAdminCompanyAdminContactTitle: false,
        adminConsoleLoginEmail: false,
        cpaAdminCompanyAdminContactNumber: false,
        cpaAdminCompanyAdminContactNumberExtension: false,
    });

    const { data: memberData, loading: memberLoading } = useGetChamberMemberQuery({
        variables: { chamberMemberId: chamberMemberId || "" },
        skip: !chamberMemberId,
    });

    const { data: userData } = useGetUserQuery({
        variables: { userId: (chamberMemberFromAuth as any)?.userId || "" },
        skip: !(chamberMemberFromAuth as any)?.userId,
    });

    // Wave C decision 3 / ACON-2: on CMP chambers only the Primary Contact
    // sees and edits the company contacts (design Company Contacts cards).
    // CPA-only members keep the original contact sections.
    // The signed-in member's own access (Cognito subject). The getUser query
    // above is keyed on a field the member record does not carry.
    const { access: memberAccess, user: memberUser } = useMemberDashboardAccess();
    const showLegacyContactCards = !!memberAccess && !memberAccess.cmpEnabled;
    const showCmpContactCards = !!memberAccess?.cmpEnabled && !!memberAccess.isPrimaryContact;
    const showContactCards = showLegacyContactCards || showCmpContactCards;
    const [contactsSavedAt, setContactsSavedAt] = useState<number>();

    const isPayerActive = !!(
        userData?.getUser?.chamberMemberInfo?.activeMemberProfileAndPerksBannersSettings ||
        memberData?.getChamberMember?.activeMemberProfileAndPerksBannersSettings
    );

    const [updateChamberMember] = useUpdateChamberMemberMutation();
    const [changeAdminEmail] = useChamberMemberChangeAdminConsoleLoginEmailMutation();

    // Banner state
    const [bannerModalOpen, setBannerModalOpen] = useState(false);
    const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [contactChamberOpen, setContactChamberOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number>(-1);

    // IDA Generate states
    const [idaDialogOpen, setIdaDialogOpen] = useState(false);
    const [idaKeyDetails, setIdaKeyDetails] = useState("");
    const [idaGeneratedText, setIdaGeneratedText] = useState("");
    const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
    const [idaSuccessDialogOpen, setIdaSuccessDialogOpen] = useState(false);
    const [descriptionGeneratedByIda, setDescriptionGeneratedByIda] = useState(false);
    const [previewBannersList, setPreviewBannersList] = useState<
        Array<{ image: string | null; videoUrl: string | null; redirectUrl: string | null }>
    >([]);
    const [bannersData, setBannersData] = useState<
        Array<{ image: string | null; videoUrl: string | null; redirectUrl: string | null }>
    >([
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
    ]);

    const handleOpenBannerModal = (index: number) => {
        setCurrentBannerIndex(index);
        setBannerModalOpen(true);
    };

    const handleSaveBanner = (data: { image: string | null; videoUrl: string | null; redirectUrl: string | null }) => {
        const newBannersData = [...bannersData];
        newBannersData[currentBannerIndex - 1] = data;
        setBannersData(newBannersData);
        setBannerModalOpen(false);
    };

    const handleMoveBanner = (index: number, direction: 'left' | 'right') => {
        const newBannersData = [...bannersData];
        const targetIndex = direction === 'left' ? index - 1 : index + 1;
        if (targetIndex >= 0 && targetIndex < newBannersData.length) {
            const temp = newBannersData[index];
            newBannersData[index] = newBannersData[targetIndex];
            newBannersData[targetIndex] = temp;
            setBannersData(newBannersData);
        }
    };

    const handleDeleteBanner = (index: number) => {
        setPendingDeleteIndex(index);
        setDeleteModalOpen(true);
    };

    const confirmDeleteBanner = () => {
        const newBannersData = [...bannersData];
        newBannersData[pendingDeleteIndex] = { image: null, videoUrl: null, redirectUrl: null };
        setBannersData(newBannersData);
        setDeleteModalOpen(false);
        setPendingDeleteIndex(-1);
    };

    const handlePreviewBanners = () => {
        setPreviewBannersList(bannersData);
        setPreviewModalOpen(true);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialValues: any = useMemo(() => ({
        id: "",
        chamberId: "",
        categoryId: [],
        name: "",
        description: "",
        website: "",
        facebook: "",
        instagram: "",
        threads: "",
        linkedin: "",
        twitter: "",
        tiktok: "",
        youtube: "",
        googleReviewLink: "",
        address: {
            city: "",
            postalCode: "",
            province: "",
            streetAddress: "",
        },
        phoneNumber: "",
        phoneNumberExtension: "",
        contactName: "",
        email: "",
        logo: "",
        showInApp: true,
        hideAddressFromDirectory: false,
        adminConsoleLoginEmail: "",
        primaryContactName: "",
        primaryContactTitle: "",
        primaryContactEmail: "",
        primaryContactPhoneNumber: "",
        primaryContactPhoneNumberExtension: "",
        adminConsoleContactName: "",
        cpaAdminCompanyAdminContactTitle: "",
        cpaAdminCompanyAdminContactNumber: "",
        cpaAdminCompanyAdminContactNumberExtension: "",
        cpaEmployeeAdoptionContactName: "",
        cpaEmployeeAdoptionContactTitle: "",
        cpaEmployeeAdoptionContactEmail: "",
        cpaEmployeeAdoptionContactNumber: "",
        cpaEmployeeAdoptionContactNumberExtension: "",
        customContacts: getInitialStateOfCustomContacts(null),
        isSuspended: false,
        primaryContactMobilePhoneNumber: "",
        cpaAdminCompanyAdminContactMobilePhoneNumber: "",
        ...EMPTY_CMP_CONTACT_FORM_VALUES,
    }), []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const validationSchema = useMemo(() => yup.object({
        name: yup.string().required(enumStrings.validationSchema.companyNameRequired),
        description: yup.string(),
        contactName: yup.string(),
        phoneNumber: yup.string(),
        email: yup.string().email(enumStrings.validationSchema.notValidEmail),
        // Contact fields are validated only where the contact sections show.
        ...(showContactCards ? {
            primaryContactName: yup.string().required(enumStrings.validationSchema.primaryContactNameRequired || "Primary Contact Name is required"),
            primaryContactTitle: yup.string(),
            primaryContactEmail: yup.string().email(enumStrings.validationSchema.notValidEmail).required(enumStrings.validationSchema.adminConsoleLoginEmailRequired || "Primary Contact Email is required"),
            primaryContactPhoneNumber: yup.string(),
            adminConsoleContactName: yup.string().required(enumStrings.validationSchema.dashboardContactNameRequired),
            adminConsoleLoginEmail: yup.string().email(enumStrings.validationSchema.notValidEmail).required(enumStrings.validationSchema.adminConsoleLoginEmailRequired || "Administrator Contact Email is required"),
            cpaAdminCompanyAdminContactNumber: yup.string(),
            cpaEmployeeAdoptionContactEmail: yup.string().email(enumStrings.validationSchema.notValidEmail),
            cpaEmployeeAdoptionContactNumber: yup.string(),
        } : {}),
        ...(showCmpContactCards ? {
            cmpContacts: yup.object(["accountsPayable", "marketingCommunication", "hrEmployeeEngagement", "sales"].reduce(
                (shape, contactId) => ({ ...shape, [contactId]: yup.object({ email: yup.string().email(enumStrings.validationSchema.notValidEmail) }) }),
                {} as Record<string, yup.AnyObjectSchema>,
            )),
        } : {}),
        categoryId: yup.array().min(1, enumStrings.validationSchema.categoryRequired),
        address: yup.object({
            province: yup.string(),
            city: yup.string(),
            postalCode: yup.string(),
            streetAddress: yup.string(),
        }),
    }), [enumStrings, showContactCards, showCmpContactCards]);

    const formik = useFormik({
        initialValues: initialValues as any,
        validationSchema,
        onSubmit: async (values: any) => {
            const orig = originalAdminContactRef.current;
            const hasAdminContactChanged =
                values.adminConsoleContactName !== orig.adminConsoleContactName ||
                values.cpaAdminCompanyAdminContactTitle !== orig.cpaAdminCompanyAdminContactTitle ||
                values.adminConsoleLoginEmail !== orig.adminConsoleLoginEmail ||
                values.cpaAdminCompanyAdminContactNumber !== orig.cpaAdminCompanyAdminContactNumber ||
                values.cpaAdminCompanyAdminContactNumberExtension !== orig.cpaAdminCompanyAdminContactNumberExtension;

            if (hasAdminContactChanged) {
                setAdminContactChanged(true);
                setConfirmDialogOpen(true);
            } else {
                setAdminContactChanged(false);
                handleConfirmUpdate();
            }
        },
    });

    // Scroll to first validation error on submit
    useEffect(() => {
        if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
            const firstErrorField = document.querySelector('.Mui-error, .MuiFormHelperText-root.Mui-error');
            if (firstErrorField) {
                const container = firstErrorField.closest('.MuiFormControl-root') || firstErrorField;
                container.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.submitCount, formik.errors]);

    const handleConfirmUpdate = async () => {
        setConfirmDialogOpen(false);
        const input = formik.values;
        let loginReplaced = false;

        if (!chamberInfo || !chamberInfo.id) {
            toast.error(enumStrings.toastMessages.unableToUpdateMember);
            return;
        }

        setLoader(true);
        try {
            setButtonDisabled(true);

            await updateChamberMember({
                variables: {
                    id: chamberMemberId || "",
                    input: {
                        address: {
                            city: input.address.city || "",
                            postalCode: input.address.postalCode || "",
                            province: input.address.province || "",
                            streetAddress: input.address.streetAddress || "",
                        },
                        categoryId: input.categoryId,
                        chamberId: chamberInfo.id,
                        description: (input.description || "").trim(),
                        contactName: (input.contactName || "").trim(),
                        email: (input.email || "").trim().toLowerCase(),
                        phoneNumber: input.phoneNumber,
                        phoneNumberExtension: input.phoneNumberExtension || "",
                        primaryContactName: (input.primaryContactName || "").trim(),
                        primaryContactTitle: (input.primaryContactTitle || "").trim(),
                        primaryContactEmail: (input.primaryContactEmail || "").trim().toLowerCase(),
                        primaryContactPhoneNumber: input.primaryContactPhoneNumber || "",
                        primaryContactPhoneNumberExtension: input.primaryContactPhoneNumberExtension || "",
                        showInApp: input.showInApp,
                        hideAddressFromDirectory: input.hideAddressFromDirectory ?? false,
                        name: input.name.trim(),
                        website: input.website,
                        facebook: input.facebook,
                        instagram: input.instagram,
                        threads: input.threads,
                        linkedin: input.linkedin,
                        twitter: input.twitter,
                        tiktok: input.tiktok,
                        youtube: input.youtube,
                        googleReviewLink: input.googleReviewLink,
                        logo: input.logo,
                        customContacts: (input.customContacts || []).map((contact: any) => {
                            const { __typename, ...cleanContact } = contact;
                            if (cleanContact.jobTitle === "Marketing/Advertising") {
                                return {
                                    ...cleanContact,
                                    name: input.adminConsoleContactName,
                                    email: input.adminConsoleLoginEmail,
                                    phoneNumber: input.cpaAdminCompanyAdminContactNumber,
                                };
                            }
                            if (cleanContact.jobTitle === "HR/Employee Wellness Personnel") {
                                return {
                                    ...cleanContact,
                                    name: input.cpaEmployeeAdoptionContactName,
                                    email: input.cpaEmployeeAdoptionContactEmail,
                                    phoneNumber: input.cpaEmployeeAdoptionContactNumber,
                                };
                            }
                            return cleanContact;
                        }),
                        id: chamberMemberId || "",
                        adminConsoleContactName: (input.adminConsoleContactName || "").trim(),
                        cpaAdminCompanyAdminContactTitle: (input.cpaAdminCompanyAdminContactTitle || "").trim(),
                        cpaAdminCompanyAdminContactNumber: input.cpaAdminCompanyAdminContactNumber || "",
                        cpaAdminCompanyAdminContactNumberExtension: input.cpaAdminCompanyAdminContactNumberExtension || "",
                        cpaEmployeeAdoptionContactName: (input.cpaEmployeeAdoptionContactName || "").trim(),
                        cpaEmployeeAdoptionContactTitle: (input.cpaEmployeeAdoptionContactTitle || "").trim(),
                        cpaEmployeeAdoptionContactEmail: (input.cpaEmployeeAdoptionContactEmail || "").trim().toLowerCase(),
                        cpaEmployeeAdoptionContactNumber: input.cpaEmployeeAdoptionContactNumber || "",
                        cpaEmployeeAdoptionContactNumberExtension: input.cpaEmployeeAdoptionContactNumberExtension || "",
                        ...(showCmpContactCards ? {
                            primaryContactMobilePhoneNumber: input.primaryContactMobilePhoneNumber || "",
                            cpaAdminCompanyAdminContactMobilePhoneNumber: input.cpaAdminCompanyAdminContactMobilePhoneNumber || "",
                            // Only the contact cards; the backend keeps every other extension field.
                            cmpExtension: buildCmpContactExtensionInput(input),
                        } : {}),
                        banner: bannersData
                            .map((b, i) => {
                                if (b.image) {
                                    return {
                                        contentUrl: b.image,
                                        imageLink: b.redirectUrl || "",
                                        contentType: BannerContentTypeEnum.Image,
                                        position: i,
                                    };
                                }
                                if (b.videoUrl) {
                                    return {
                                        contentUrl: b.videoUrl,
                                        imageLink: b.redirectUrl || "",
                                        contentType: BannerContentTypeEnum.Video,
                                        position: i,
                                    };
                                }
                                return null;
                            })
                            .filter((b): b is NonNullable<typeof b> => b !== null),
                    } as any,
                },
                refetchQueries: ["getChamberMember", "getAllChamberMembers"],
            });

            // Save the profile first. With an old company login, changing the
            // Administrator email replaces the signed-in login, so a later
            // profile save would fail and lose the other changes.
            const currentAdminEmail =
                memberData?.getChamberMember?.adminConsoleLoginEmail ||
                originalAdminContactRef.current.adminConsoleLoginEmail ||
                "";
            const newAdminEmail = (input.adminConsoleLoginEmail || "").trim().toLowerCase();
            if (showContactCards && newAdminEmail && newAdminEmail !== currentAdminEmail.toLowerCase()) {
                const { data: emailChangeData } = await changeAdminEmail({
                    variables: {
                        chamberMemberId: chamberMemberId || "",
                        currentAdminConsoleLoginEmail: currentAdminEmail,
                        newAdminConsoleLoginEmail: newAdminEmail,
                    },
                    refetchQueries: [
                        "getChamberMember",
                        "getAllChamberMembers",
                        "getUser",
                    ],
                    awaitRefetchQueries: true,
                });

                if (emailChangeData?.chamberMemberChangeAdminConsoleLoginEmail?.adminConsoleLoginEmailChangedSuccessfully === false) {
                    const failureReason = emailChangeData.chamberMemberChangeAdminConsoleLoginEmail.failureReason || "";
                    toast.error(failureReason || enumStrings.toastMessages.failedToUpdateProfile);
                    setLoader(false);
                    setButtonDisabled(false);
                    return;
                }
                // A new password is returned only when the old company login
                // moved to the new email. With per-contact logins the signed-in
                // login stays, so the member stays signed in.
                loginReplaced = !!emailChangeData?.chamberMemberChangeAdminConsoleLoginEmail?.password;
            }
            setAdminContactChanged(loginReplaced);


            setLoader(false);
            setButtonDisabled(false);
            savedValuesRef.current = JSON.stringify(formik.values);
            setContactsSavedAt(Date.now());
            setUpdateSuccessDialogOpen(true);
        } catch (error) {
            console.error(error);
            const serverMessage = (error as { graphQLErrors?: { message?: string }[] })?.graphQLErrors?.[0]?.message || "";
            toast.error(serverMessage.startsWith(OWN_LOGIN_EMAIL_REFUSAL) ? serverMessage : enumStrings.toastMessages.failedToUpdateProfile);
            setLoader(false);
            setButtonDisabled(false);
        }
    };

    useEffect(() => {
        if (memberData?.getChamberMember && !dataLoaded) {
            setDataLoaded(true);
            const member = memberData.getChamberMember;
            formik.setValues({
                id: member.id || "",
                chamberId: member.chamberId || "",
                categoryId: member.categoryId || [],
                name: member.name || "",
                description: member.description || "",
                website: member.website || "",
                facebook: member.facebook || "",
                instagram: member.instagram || "",
                threads: member.threads || "",
                linkedin: member.linkedin || "",
                twitter: member.twitter || "",
                tiktok: member.tiktok || "",
                youtube: member.youtube || "",
                googleReviewLink: member.googleReviewLink || "",
                address: {
                    city: member.address?.city || "",
                    postalCode: member.address?.postalCode || "",
                    province: member.address?.province || "",
                    streetAddress: member.address?.streetAddress || "",
                },
                phoneNumber: member.phoneNumber || "",
                phoneNumberExtension: (member as any).phoneNumberExtension || "",
                contactName: member.contactName || "",
                email: member.email || "",
                logo: member.logo || "",
                showInApp: member.showInApp ?? true,
                hideAddressFromDirectory: member.hideAddressFromDirectory ?? false,
                adminConsoleLoginEmail: member.adminConsoleLoginEmail || member.email || "",
                primaryContactName: (member as any).primaryContactName || "",
                primaryContactTitle: (member as any).primaryContactTitle || "",
                primaryContactEmail: (member as any).primaryContactEmail || "",
                primaryContactPhoneNumber: (member as any).primaryContactPhoneNumber || "",
                primaryContactPhoneNumberExtension: (member as any).primaryContactPhoneNumberExtension || "",
                adminConsoleContactName: member.adminConsoleContactName || "",
                cpaAdminCompanyAdminContactTitle: (member as any).cpaAdminCompanyAdminContactTitle || "",
                cpaAdminCompanyAdminContactNumber: (member as any).cpaAdminCompanyAdminContactNumber || "",
                cpaAdminCompanyAdminContactNumberExtension: (member as any).cpaAdminCompanyAdminContactNumberExtension || "",
                cpaEmployeeAdoptionContactName: (member as any).cpaEmployeeAdoptionContactName || "",
                cpaEmployeeAdoptionContactTitle: (member as any).cpaEmployeeAdoptionContactTitle || "",
                cpaEmployeeAdoptionContactEmail: (member as any).cpaEmployeeAdoptionContactEmail || "",
                cpaEmployeeAdoptionContactNumber: (member as any).cpaEmployeeAdoptionContactNumber || "",
                cpaEmployeeAdoptionContactNumberExtension: (member as any).cpaEmployeeAdoptionContactNumberExtension || "",
                customContacts: member.customContacts && member.customContacts.length > 0
                    ? member.customContacts
                    : getInitialStateOfCustomContacts(null),
                isSuspended: member.isSuspended || false,
                primaryContactMobilePhoneNumber: (member as any).primaryContactMobilePhoneNumber || "",
                cpaAdminCompanyAdminContactMobilePhoneNumber: (member as any).cpaAdminCompanyAdminContactMobilePhoneNumber || "",
                ...getCmpContactFormValues(member as any),
            });
            savedValuesRef.current = JSON.stringify({
                id: member.id || "",
                chamberId: member.chamberId || "",
                categoryId: member.categoryId || [],
                name: member.name || "",
                description: member.description || "",
                website: member.website || "",
                facebook: member.facebook || "",
                instagram: member.instagram || "",
                threads: member.threads || "",
                linkedin: member.linkedin || "",
                twitter: member.twitter || "",
                tiktok: member.tiktok || "",
                youtube: member.youtube || "",
                googleReviewLink: member.googleReviewLink || "",
                address: { city: member.address?.city || "", postalCode: member.address?.postalCode || "", province: member.address?.province || "", streetAddress: member.address?.streetAddress || "" },
                phoneNumber: member.phoneNumber || "",
                phoneNumberExtension: (member as any).phoneNumberExtension || "",
                contactName: member.contactName || "",
                email: member.email || "",
                logo: member.logo || "",
                showInApp: member.showInApp ?? true,
                hideAddressFromDirectory: member.hideAddressFromDirectory ?? false,
                adminConsoleLoginEmail: member.adminConsoleLoginEmail || member.email || "",
                primaryContactName: (member as any).primaryContactName || "",
                primaryContactTitle: (member as any).primaryContactTitle || "",
                primaryContactEmail: (member as any).primaryContactEmail || "",
                primaryContactPhoneNumber: (member as any).primaryContactPhoneNumber || "",
                primaryContactPhoneNumberExtension: (member as any).primaryContactPhoneNumberExtension || "",
                adminConsoleContactName: member.adminConsoleContactName || "",
                cpaAdminCompanyAdminContactTitle: (member as any).cpaAdminCompanyAdminContactTitle || "",
                cpaAdminCompanyAdminContactNumber: (member as any).cpaAdminCompanyAdminContactNumber || "",
                cpaAdminCompanyAdminContactNumberExtension: (member as any).cpaAdminCompanyAdminContactNumberExtension || "",
                cpaEmployeeAdoptionContactName: (member as any).cpaEmployeeAdoptionContactName || "",
                cpaEmployeeAdoptionContactTitle: (member as any).cpaEmployeeAdoptionContactTitle || "",
                cpaEmployeeAdoptionContactEmail: (member as any).cpaEmployeeAdoptionContactEmail || "",
                cpaEmployeeAdoptionContactNumber: (member as any).cpaEmployeeAdoptionContactNumber || "",
                cpaEmployeeAdoptionContactNumberExtension: (member as any).cpaEmployeeAdoptionContactNumberExtension || "",
                customContacts: member.customContacts && member.customContacts.length > 0 ? member.customContacts : getInitialStateOfCustomContacts(null),
                isSuspended: member.isSuspended || false,
                primaryContactMobilePhoneNumber: (member as any).primaryContactMobilePhoneNumber || "",
                cpaAdminCompanyAdminContactMobilePhoneNumber: (member as any).cpaAdminCompanyAdminContactMobilePhoneNumber || "",
                ...getCmpContactFormValues(member as any),
            });

            // Extract additional contacts from customContacts
            if (member.customContacts && member.customContacts.length > 0) {
                const adminContact = member.customContacts.find(c => c.jobTitle === "Marketing/Advertising");
                const employeeAdoptionContact = member.customContacts.find(c => c.jobTitle === "HR/Employee Wellness Personnel");

                if (adminContact) {
                    if (adminContact.phoneNumber) {
                        formik.setFieldValue("cpaAdminCompanyAdminContactNumber", adminContact.phoneNumber);
                    }
                }

                if (employeeAdoptionContact) {
                    if (employeeAdoptionContact.name) {
                        formik.setFieldValue("cpaEmployeeAdoptionContactName", employeeAdoptionContact.name);
                    }
                    if (employeeAdoptionContact.email) {
                        formik.setFieldValue("cpaEmployeeAdoptionContactEmail", employeeAdoptionContact.email);
                    }
                    if (employeeAdoptionContact.phoneNumber) {
                        formik.setFieldValue("cpaEmployeeAdoptionContactNumber", employeeAdoptionContact.phoneNumber);
                    }
                }

                // Save original admin contact values for change detection
                originalAdminContactRef.current = {
                    adminConsoleContactName: member.adminConsoleContactName || "",
                    cpaAdminCompanyAdminContactTitle: (member as any).cpaAdminCompanyAdminContactTitle || "",
                    adminConsoleLoginEmail: member.adminConsoleLoginEmail || member.email || "",
                    cpaAdminCompanyAdminContactNumber: adminContact?.phoneNumber || (member as any).cpaAdminCompanyAdminContactNumber || member.phoneNumber || "",
                    cpaAdminCompanyAdminContactNumberExtension: (member as any).cpaAdminCompanyAdminContactNumberExtension || "",
                };
            } else {
                // No custom contacts — save defaults
                originalAdminContactRef.current = {
                    adminConsoleContactName: member.adminConsoleContactName || "",
                    cpaAdminCompanyAdminContactTitle: (member as any).cpaAdminCompanyAdminContactTitle || "",
                    adminConsoleLoginEmail: member.adminConsoleLoginEmail || member.email || "",
                    cpaAdminCompanyAdminContactNumber: (member as any).cpaAdminCompanyAdminContactNumber || member.phoneNumber || "",
                    cpaAdminCompanyAdminContactNumberExtension: (member as any).cpaAdminCompanyAdminContactNumberExtension || "",
                };
            }

            // Detect which primary/admin field pairs initially match (linked state)
            const pName = (member as any).primaryContactName || "";
            const aName = member.adminConsoleContactName || "";
            const pTitle = (member as any).primaryContactTitle || "";
            const aTitle = (member as any).cpaAdminCompanyAdminContactTitle || "";
            const pEmail = (member as any).primaryContactEmail || "";
            const aEmail = member.adminConsoleLoginEmail || member.email || "";
            const pPhone = (member as any).primaryContactPhoneNumber || "";
            const aPhone = (member as any).cpaAdminCompanyAdminContactNumber || "";
            const pPhoneExt = (member as any).primaryContactPhoneNumberExtension || "";
            const aPhoneExt = (member as any).cpaAdminCompanyAdminContactNumberExtension || "";

            fieldsInitiallyLinked.current = {
                adminConsoleContactName: pName === aName && pName !== "",
                cpaAdminCompanyAdminContactTitle: pTitle === aTitle && pTitle !== "",
                adminConsoleLoginEmail: pEmail === aEmail && pEmail !== "",
                cpaAdminCompanyAdminContactNumber: pPhone === aPhone && pPhone !== "",
                cpaAdminCompanyAdminContactNumberExtension: pPhoneExt === aPhoneExt && pPhoneExt !== "",
            };

            // Reset touched state when member data reloads
            adminFieldsTouched.current = {
                adminConsoleContactName: false,
                cpaAdminCompanyAdminContactTitle: false,
                adminConsoleLoginEmail: false,
                cpaAdminCompanyAdminContactNumber: false,
                cpaAdminCompanyAdminContactNumberExtension: false,
            };

            // Load banners if they exist
            if ((member as any).banner && Array.isArray((member as any).banner)) {
                const loadedBanners = (member as any).banner;
                const newBannersData = [
                    { image: null, videoUrl: null, redirectUrl: null },
                    { image: null, videoUrl: null, redirectUrl: null },
                    { image: null, videoUrl: null, redirectUrl: null },
                    { image: null, videoUrl: null, redirectUrl: null },
                ];
                loadedBanners.forEach((banner: any, idx: number) => {
                    if (idx < 4) {
                        if (banner.contentType === 'IMAGE' && banner.contentUrl) {
                            newBannersData[idx] = { image: banner.contentUrl, videoUrl: null, redirectUrl: banner.imageLink || null };
                        } else if (banner.contentType === 'VIDEO' && banner.contentUrl) {
                            newBannersData[idx] = { image: null, videoUrl: banner.contentUrl, redirectUrl: banner.imageLink || null };
                        }
                    }
                });
                setBannersData(newBannersData);
            }

            if (member.categoryId && member.categoryId.length >= 2) {
                setCategoryDisable(true);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberData, dataLoaded]);

    // Auto-sync primary contact → admin contact fields
    // Only syncs when fields were initially linked AND user hasn't focused the admin field
    useEffect(() => {
        if (fieldsInitiallyLinked.current.adminConsoleContactName && !adminFieldsTouched.current.adminConsoleContactName) {
            formik.setFieldValue("adminConsoleContactName", formik.values.primaryContactName);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.primaryContactName]);

    useEffect(() => {
        if (fieldsInitiallyLinked.current.cpaAdminCompanyAdminContactTitle && !adminFieldsTouched.current.cpaAdminCompanyAdminContactTitle) {
            formik.setFieldValue("cpaAdminCompanyAdminContactTitle", formik.values.primaryContactTitle);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.primaryContactTitle]);

    useEffect(() => {
        if (fieldsInitiallyLinked.current.cpaAdminCompanyAdminContactNumber && !adminFieldsTouched.current.cpaAdminCompanyAdminContactNumber) {
            formik.setFieldValue("cpaAdminCompanyAdminContactNumber", formik.values.primaryContactPhoneNumber);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.primaryContactPhoneNumber]);

    useEffect(() => {
        if (fieldsInitiallyLinked.current.cpaAdminCompanyAdminContactNumberExtension && !adminFieldsTouched.current.cpaAdminCompanyAdminContactNumberExtension) {
            formik.setFieldValue("cpaAdminCompanyAdminContactNumberExtension", formik.values.primaryContactPhoneNumberExtension);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.primaryContactPhoneNumberExtension]);

    const handleCategoriesChange = (event: { target: { value: any } }) => {
        const value = event.target.value;
        formik.setFieldValue("categoryId", value);
        setCategoryDisable(value.length >= 2);
    };

    const handleDelete = (value: string) => {
        const newArray = _without(formik.values.categoryId, value);
        formik.setFieldValue("categoryId", newArray);
        setCategoryDisable(newArray.length >= 2);
    };

    const handleCreateBase64 = useCallback(
        async (e) => {
            const file = e?.target?.files?.[0];
            if (!file) return;
            const fileReader = new FileReader();
            fileReader.readAsDataURL(file);
            fileReader.onload = () => {
                formik.setFieldValue("logo", fileReader.result);
            };
        },
        [formik]
    );

    const deleteLogo = (e: { preventDefault: () => void }) => {
        e.preventDefault();
        formik.setFieldValue("logo", "");
    };

    if (memberLoading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress style={{ color: "white" }} />
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <UnsavedChangesPrompt isDirty={savedValuesRef.current !== "" && JSON.stringify(formik.values) !== savedValuesRef.current} />
            <ToastContainer position="top-right" autoClose={8000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} />
            <div className={classes.titleContent}>
                <div className={classes.titleRow}>
                    <div className={classes.title}>
                        <span>{enumStrings.manageMemberProfile.title}</span>
                        <span className={classes.subtitle}>
                            {enumStrings.manageMemberProfile.subtitle}
                        </span>
                        <div style={{ height: 6 }} />
                        <div className={classes.pageHeaderButtonRow}>
                            <VideoTutorialButton
                                tutorialUrl="https://share.synthesia.io/embeds/videos/e9ae72e1-71f3-44fb-a5bc-d71d35cab428"
                            />
                            <ExportButton
                                customOnClick={() => openMemberInstructionPdf("Member Dashboard -  Manage Company Profile.pdf")}
                                icon={<InfoOutlinedIcon style={{ color: theme.palette.primary.main, marginRight: "10px", width: "24px", height: "24px" }} />}
                                label={enumStrings.manageChamberEngage.viewInstructions}
                                disablePadding
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className={classes.content}>
                <form onSubmit={formik.handleSubmit} autoComplete="off">
                    {/* Company Profile Information Section */}
                    <div className={classes.formSection}>
                        <div className={classes.sectionHeader}>
                            <p className={classes.sectionTitle}>{enumStrings.createMemberForm.companyProfileInformation}</p>
                        </div>
                        <p className={classes.sectionHelperText}>
                            {enumStrings.createMemberForm.companyProfileInfoDescMember}
                        </p>

                        <div className={classes.companyInfoGrid}>
                            {/* Company Name - Left Row 1 */}
                            <div style={{ gridArea: "companyName" }}>
                                <div className={classes.fieldLabel}>
                                    <span>{enumStrings.createMemberForm.companyName}<span className={classes.requiredAsterisk}>*</span></span>
                                </div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.abcPlumbing}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                />
                            </div>

                            {/* Company Description - Right Row 1+2 (spans two rows) */}
                            <div style={{ gridArea: "description" }}>
                                <div className={classes.fieldLabel}>
                                    <span>{enumStrings.createMemberForm.companyDescription}</span>
                                </div>
                                <div className={classes.descriptionHelperText} dangerouslySetInnerHTML={{ __html: enumStrings.createMemberForm.companyDescriptionHelper }} />
                                <div style={{
                                    border: descriptionGeneratedByIda
                                        ? `2px solid ${theme.palette.success.main}`
                                        : `1px solid ${theme.palette.text.primary}`,
                                    borderRadius: "8px",
                                    backgroundColor: "#FFFFFF",
                                    padding: "16px",
                                    display: "flex",
                                    flexDirection: "column",
                                }}>
                                    {descriptionGeneratedByIda && (
                                        <div style={{
                                            fontFamily: "DM Sans, sans-serif",
                                            fontWeight: 700,
                                            fontSize: "10px",
                                            lineHeight: "15px",
                                            color: theme.palette.success.main,
                                            marginBottom: "4px",
                                        }}>
                                            {enumStrings.createMemberForm.idaCompanyDescriptionLabel}
                                        </div>
                                    )}
                                    <TextField
                                        placeholder={enumStrings.createMemberForm.placeholders.enterCompanyDescription}
                                        variant="standard"
                                        multiline
                                        minRows={3}
                                        maxRows={3}
                                        name="description"
                                        value={formik.values.description}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            if (descriptionGeneratedByIda) {
                                                setDescriptionGeneratedByIda(false);
                                            }
                                        }}
                                        inputProps={{
                                            maxLength: CHARACTER_LIMIT_DESCRIPTION,
                                            style: { overflow: "auto" },
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                        style={{ flex: 1 }}
                                        fullWidth
                                    />
                                </div>
                                {/* IDA AI Helper Section - below the textarea: counter left, icon + generate right */}
                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: "12px", marginTop: "12px", flexWrap: "wrap" }}>
                                    <div style={{ fontSize: "14px", color: theme.palette.text.secondary, textAlign: "left", fontFamily: "Inter, sans-serif" }}>
                                        {(formik.values.description || "").length}/{CHARACTER_LIMIT_DESCRIPTION} {enumStrings.createMemberForm.characters}
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "12px" }}>
                                        <img src={IdaChatbotIcon} alt="IDA" style={{ width: "40px", height: "40px", objectFit: "contain" }} />
                                        {chamberInfo?.isSandbox ? (
                                            <Button
                                                variant="contained"
                                                onClick={() => {
                                                    setIdaDialogOpen(true);
                                                }}
                                                style={{
                                                    backgroundColor: "#4CAF50",
                                                    color: "#FFFFFF",
                                                    fontSize: "14px",
                                                    fontWeight: 700,
                                                    borderRadius: "8px",
                                                    padding: "8px 24px",
                                                    textTransform: "uppercase",
                                                    minHeight: "36px",
                                                    boxShadow: "none",
                                                }}
                                            >
                                                {descriptionGeneratedByIda ? enumStrings.idaGenerate.idaRegenerate : enumStrings.idaGenerate.generateButton}
                                            </Button>
                                        ) : (
                                            <span style={{
                                                backgroundColor: "#E53935",
                                                color: "#FFFFFF",
                                                border: "1px solid #E53935",
                                                borderRadius: "10px",
                                                padding: "6px 14px",
                                                fontSize: "12px",
                                                fontWeight: 700,
                                                whiteSpace: "nowrap" as const,
                                                display: "inline-block",
                                            }}>
                                                COMING SOON
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Company Website - Left Row 2 */}
                            <div style={{ gridArea: "companyWebsite" }}>
                                <div className={classes.fieldLabel}>
                                    <span>{enumStrings.createMemberForm.companyWebsite}</span>
                                </div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterCompanyWebsite}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="website"
                                    value={formik.values.website}
                                    onChange={formik.handleChange}
                                />
                            </div>

                            {/* Main Company Contact Name - Left Row 3 */}
                            <div style={{ gridArea: "contactName" }}>
                                <div className={classes.fieldLabel}>
                                    <span>{enumStrings.createMemberForm.mainCompanyContactName}</span>
                                </div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterCompanyContactName}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="contactName"
                                    value={formik.values.contactName}
                                    onChange={formik.handleChange}
                                    error={formik.touched.contactName && Boolean(formik.errors.contactName)}
                                    helperText={formik.touched.contactName && formik.errors.contactName}
                                />
                            </div>

                            {/* Company Contact Phone + Ext - Right Row 3 */}
                            <div style={{ gridArea: "phone" }}>
                                <div style={{ display: "flex", gap: "10px" }}>
                                    <div className={classes.fieldLabel} style={{ flex: 1 }}>
                                        <span>{enumStrings.createMemberForm.companyContactPhone}</span>
                                    </div>
                                    <div className={classes.fieldLabel} style={{ width: "100px" }}>
                                        <span>{enumStrings.createMemberForm?.extensionNo}</span>
                                    </div>
                                </div>
                                <div style={{ display: "flex", gap: "10px" }}>
                                    <TextField
                                        placeholder={enumStrings.createMemberForm.placeholders.enterCompanyContactPhone}
                                        className={classes.companyInput}
                                        variant="outlined"
                                        inputProps={{ autoComplete: "new-password" }}
                                        name="phoneNumber"
                                        value={formik.values.phoneNumber}
                                        onChange={formik.handleChange}
                                        error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                                        helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                                        InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                                        style={{ flex: 1 }}
                                    />
                                    <TextField
                                        placeholder={enumStrings.createMemberForm?.extensionPlaceholder || "00000"}
                                        className={classes.companyInput}
                                        variant="outlined"
                                        inputProps={{ autoComplete: "new-password" }}
                                        name="phoneNumberExtension"
                                        value={formik.values.phoneNumberExtension}
                                        onChange={formik.handleChange}
                                        style={{ width: "100px" }}
                                    />
                                </div>
                            </div>

                            {/* Main Company Contact Email - Left Row 4 */}
                            <div style={{ gridArea: "contactEmail" }}>
                                <div className={classes.fieldLabel}>
                                    <span>{enumStrings.createMemberForm.mainCompanyContactEmail}</span>
                                </div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterCompanyContactEmail}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                />
                            </div>

                            {/* Google Review URL - Right Row 4 */}
                            <div style={{ gridArea: "googleReview" }}>
                                <div className={classes.fieldLabel}>
                                    <span>{enumStrings.createMemberForm.googleReviewUrl}</span>
                                </div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterGoogleReviewUrl}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="googleReviewLink"
                                    value={formik.values.googleReviewLink}
                                    onChange={formik.handleChange}
                                />
                            </div>

                            {/* Company Logo - Left Row 5 */}
                            <div style={{ gridArea: "logo" }}>
                                <div className={classes.fieldLabel}>
                                    <span>{enumStrings.createMemberForm.logo}</span>
                                </div>
                                <div className={classes.logoHelperText}>
                                    {enumStrings.createMemberForm.logoHelperText}
                                </div>
                                <LogoPicker
                                    logo={formik.values.logo}
                                    deleteLogo={deleteLogo}
                                    base64={(e: any) => handleCreateBase64(e)}
                                />
                            </div>

                            {/* Chamber Perks App Categories - Right Row 5 */}
                            <div style={{ gridArea: "categories" }}>
                                <div className={classes.fieldLabel}>
                                    <span style={{ display: "flex", alignItems: "flex-start", gap: "4px" }}>
                                        {enumStrings.createMemberForm.chamberPerksCategories} <span className={classes.requiredAsterisk}>*</span>
                                        <img src={IdaChatbotIcon} alt="IDA" style={{ width: "73px", height: "57px", objectFit: "contain", marginTop: "-15px" }} />
                                    </span>
                                </div>
                                <div className={classes.categoryHelperText} dangerouslySetInnerHTML={{ __html: enumStrings.createMemberForm.categoriesHelperText }} />
                                <Categories
                                    selectedCategories={formik.values.categoryId}
                                    handleCategoriesChange={handleCategoriesChange}
                                    disableCategories={categoryDisable}
                                    handleDelete={handleDelete}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Company Address Section */}
                    <div className={classes.formSection}>
                        <div className={classes.sectionHeader}>
                            <p className={classes.sectionTitle}>{enumStrings.createMemberForm.companyAddress}</p>
                        </div>
                        <p className={classes.sectionHelperText}>
                            {enumStrings.createMemberForm.companyAddressDescMember}
                        </p>
                        <FormControlLabel
                            className={classes.hideAddressCheckbox}
                            control={
                                <Checkbox
                                    checked={formik.values.hideAddressFromDirectory}
                                    onChange={(e) => formik.setFieldValue("hideAddressFromDirectory", e.target.checked)}
                                    style={{ color: "#666666" }}
                                />
                            }
                            label={enumStrings.createMemberForm.hideAddressCheckbox}
                        />
                        <div className={classes.formRow}>
                            <div>
                                <div className={classes.fieldLabel}>{enumStrings.createMemberForm.streetAddress}</div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterStreetAddress}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="address.streetAddress"
                                    value={formik.values.address.streetAddress}
                                    onChange={formik.handleChange}
                                />
                            </div>
                            <div>
                                <div className={classes.fieldLabel}>{enumStrings.createMemberForm.city}</div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterCity}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="address.city"
                                    value={formik.values.address.city}
                                    onChange={formik.handleChange}
                                    error={!!(getIn(formik.touched, "address.city") && (formik.errors as any).address?.city)}
                                    helperText={getIn(formik.touched, "address.city") && (formik.errors as any).address?.city}
                                />
                            </div>
                        </div>
                        <div className={classes.formRow}>
                            <div>
                                <div className={classes.fieldLabel}>{enumStrings.createMemberForm.provinceState}</div>
                                <ProvincePicker
                                    formikVal={formik.values.address.province}
                                    formikSetFieldValue={formik.setFieldValue}
                                    formikErrors={formik.errors.address}
                                    formikStringError={(formik.errors as any).address?.province}
                                />
                            </div>
                            <div>
                                <div className={classes.fieldLabel}>{enumStrings.createMemberForm.postalZipCode}</div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterPostalCode}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="address.postalCode"
                                    value={formik.values.address.postalCode}
                                    onChange={formik.handleChange}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Links to Social Media Section */}
                    <div className={classes.formSection}>
                        <div className={classes.sectionHeader}>
                            <p className={classes.sectionTitle}>{enumStrings.createMemberForm.linksToSocials}</p>
                        </div>
                        <p className={classes.sectionHelperText}>
                            {enumStrings.createMemberForm.socialsDescMember}
                        </p>
                        <p className={classes.sectionHelperText} style={{ fontWeight: 600 }}>
                            {enumStrings.createMemberForm.socialsDescMember2}
                        </p>
                        <div className={classes.formRow}>
                            <div>
                                <div className={classes.fieldLabel}>{enumStrings.createMemberForm.facebookUrl}</div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterFacebookUrl}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="facebook"
                                    value={formik.values.facebook}
                                    onChange={formik.handleChange}
                                />
                            </div>
                            <div>
                                <div className={classes.fieldLabel}>{enumStrings.createMemberForm.instagramUrl}</div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterInstagramUrl}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="instagram"
                                    value={formik.values.instagram}
                                    onChange={formik.handleChange}
                                />
                            </div>
                        </div>
                        <div className={classes.formRow}>
                            <div>
                                <div className={classes.fieldLabel}>{enumStrings.createMemberForm.threadsUrl}</div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterThreadsUrl}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="threads"
                                    value={formik.values.threads}
                                    onChange={formik.handleChange}
                                />
                            </div>
                            <div>
                                <div className={classes.fieldLabel}>{enumStrings.createMemberForm.linkedinUrl}</div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterLinkedinUrl}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="linkedin"
                                    value={formik.values.linkedin}
                                    onChange={formik.handleChange}
                                />
                            </div>
                        </div>
                        <div className={classes.formRow}>
                            <div>
                                <div className={classes.fieldLabel}>{enumStrings.createMemberForm.xTwitterUrl}</div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterXTwitterUrl}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="twitter"
                                    value={formik.values.twitter}
                                    onChange={formik.handleChange}
                                />
                            </div>
                            <div>
                                <div className={classes.fieldLabel}>{enumStrings.createMemberForm.tiktokUrl}</div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterTiktokUrl}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="tiktok"
                                    value={formik.values.tiktok}
                                    onChange={formik.handleChange}
                                />
                            </div>
                        </div>
                        <div className={classes.formRow}>
                            <div>
                                <div className={classes.fieldLabel}>{enumStrings.createMemberForm.youtubeUrl}</div>
                                <TextField
                                    placeholder={enumStrings.createMemberForm.placeholders.enterYoutubeUrl}
                                    className={classes.companyInput}
                                    variant="outlined"
                                    inputProps={{ autoComplete: "new-password" }}
                                    name="youtube"
                                    value={formik.values.youtube}
                                    onChange={formik.handleChange}
                                />
                            </div>
                        </div>
                    </div>

                    {showContactCards && (<>
                        {/* Company Primary Contact Section */}
                        <div className={classes.formSection}>
                            <div className={classes.sectionHeader}>
                                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.companyPrimaryContact}</p>
                            </div>
                            <p className={classes.sectionHelperText}>
                                {enumStrings.createMemberForm.primaryContactDescLine1Start}{" "}
                                <strong>{enumStrings.createMemberForm.primaryContactDescLine1Bold}</strong>
                            </p>
                            <p className={classes.sectionHelperText}>
                                {enumStrings.createMemberForm.primaryContactDescLine2Start}{" "}
                                <strong>{enumStrings.createMemberForm.primaryContactDescLine2Bold}</strong>{" "}
                                {enumStrings.createMemberForm.primaryContactDescLine2End}
                            </p>
                            <div className={classes.formRow}>
                                <div>
                                    <div className={classes.fieldLabel}>
                                        <span>{enumStrings.createMemberForm.primaryContactName}<span className={classes.requiredAsterisk}>*</span></span>
                                    </div>
                                    <TextField
                                        placeholder={enumStrings.createMemberForm.placeholders.enterPrimaryContactName}
                                        className={classes.companyInput}
                                        variant="outlined"
                                        inputProps={{ autoComplete: "new-password" }}
                                        name="primaryContactName"
                                        value={formik.values.primaryContactName}
                                        onChange={formik.handleChange}
                                        error={formik.touched.primaryContactName && Boolean(formik.errors.primaryContactName)}
                                        helperText={formik.touched.primaryContactName && formik.errors.primaryContactName}
                                    />
                                </div>
                                <div>
                                    <div className={classes.fieldLabel}>
                                        <span>{enumStrings.createMemberForm.title}</span>
                                    </div>
                                    <TextField
                                        placeholder={enumStrings.createMemberForm.placeholders.enterContactTitle}
                                        className={classes.companyInput}
                                        variant="outlined"
                                        inputProps={{ autoComplete: "new-password" }}
                                        name="primaryContactTitle"
                                        value={formik.values.primaryContactTitle}
                                        onChange={formik.handleChange}
                                        error={formik.touched.primaryContactTitle && Boolean(formik.errors.primaryContactTitle)}
                                        helperText={formik.touched.primaryContactTitle && formik.errors.primaryContactTitle}
                                    />
                                </div>
                            </div>
                            <div className={classes.formRow}>
                                <div>
                                    <div className={classes.fieldLabel}>
                                        <span>{enumStrings.createMemberForm.primaryContactEmail}<span className={classes.requiredAsterisk}>*</span></span>
                                    </div>
                                    <div style={{ fontSize: "16px", color: "#000000", marginBottom: "8px", lineHeight: "24px" }}>
                                        {enumStrings.createMemberForm.emailUsernameNote}
                                    </div>
                                    <TextField
                                        placeholder={enumStrings.createMemberForm.placeholders.enterPrimaryContactEmail}
                                        className={classes.companyInput}
                                        variant="outlined"
                                        inputProps={{ autoComplete: "new-password" }}
                                        name="primaryContactEmail"
                                        value={formik.values.primaryContactEmail}
                                        onChange={formik.handleChange}
                                        error={formik.touched.primaryContactEmail && Boolean(formik.errors.primaryContactEmail)}
                                        helperText={formik.touched.primaryContactEmail && formik.errors.primaryContactEmail}
                                    />
                                </div>
                                <div>
                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <div className={classes.fieldLabel} style={{ flex: 1 }}>
                                            <span>{enumStrings.createMemberForm.primaryContactPhone}</span>
                                        </div>
                                        <div className={classes.fieldLabel} style={{ width: "100px" }}>
                                            {enumStrings.createMemberForm.extensionNo}
                                        </div>
                                    </div>
                                    <div style={{ fontSize: "14px", marginBottom: "8px", lineHeight: "24px", visibility: "hidden" as const }}>&nbsp;</div>
                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <TextField
                                            placeholder={enumStrings.createMemberForm.placeholders.enterPrimaryContactPhone}
                                            className={classes.companyInput}
                                            variant="outlined"
                                            inputProps={{ autoComplete: "new-password" }}
                                            name="primaryContactPhoneNumber"
                                            value={formik.values.primaryContactPhoneNumber}
                                            onChange={formik.handleChange}
                                            error={formik.touched.primaryContactPhoneNumber && Boolean(formik.errors.primaryContactPhoneNumber)}
                                            helperText={formik.touched.primaryContactPhoneNumber && formik.errors.primaryContactPhoneNumber}
                                            InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                                            style={{ flex: 1 }}
                                        />
                                        <TextField
                                            placeholder={enumStrings.createMemberForm.extensionPlaceholder}
                                            className={classes.companyInput}
                                            variant="outlined"
                                            inputProps={{ autoComplete: "new-password" }}
                                            name="primaryContactPhoneNumberExtension"
                                            value={formik.values.primaryContactPhoneNumberExtension}
                                            onChange={formik.handleChange}
                                            style={{ width: "100px" }}
                                        />
                                    </div>
                                </div>
                            </div>
                            {showCmpContactCards && (
                                <div className={classes.formRow}>
                                    <div>
                                        <div className={classes.fieldLabel}>Mobile Number</div>
                                        <TextField
                                            placeholder="Mobile Number"
                                            className={classes.companyInput}
                                            variant="outlined"
                                            inputProps={{ autoComplete: "new-password" }}
                                            name="primaryContactMobilePhoneNumber"
                                            value={formik.values.primaryContactMobilePhoneNumber}
                                            onChange={formik.handleChange}
                                            InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                                        />
                                    </div>
                                    <div />
                                </div>
                            )}
                        </div>

                    </>)}

                    {showLegacyContactCards && (<>
                        {/* Chamber Perks App Company Administrator Contact Section */}
                        <div className={classes.formSection}>
                            <div className={classes.sectionHeader}>
                                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.chamberPerksAdminContact}</p>
                            </div>
                            <p className={classes.sectionHelperText}>
                                {enumStrings.createMemberForm.adminContactDescMember}
                            </p>
                            <p className={classes.sectionHelperText} style={{ fontWeight: 700 }}>
                                {enumStrings.createMemberForm.adminContactNotAppear}
                            </p>

                            <div className={classes.formRow}>
                                <div>
                                    <div className={classes.fieldLabel}>
                                        <span>{enumStrings.createMemberForm.contactName}<span className={classes.requiredAsterisk}>*</span></span>
                                    </div>
                                    <TextField
                                        placeholder={enumStrings.createMemberForm.placeholders.enterContactName}
                                        className={classes.companyInput}
                                        variant="outlined"
                                        inputProps={{ autoComplete: "new-password" }}
                                        name="adminConsoleContactName"
                                        value={formik.values.adminConsoleContactName}
                                        onFocus={() => { adminFieldsTouched.current.adminConsoleContactName = true; }}
                                        onChange={formik.handleChange}
                                        error={formik.touched.adminConsoleContactName && Boolean(formik.errors.adminConsoleContactName)}
                                        helperText={formik.touched.adminConsoleContactName && formik.errors.adminConsoleContactName}
                                    />
                                </div>
                                <div>
                                    <div className={classes.fieldLabel}>
                                        {enumStrings.createMemberForm.title}
                                    </div>
                                    <TextField
                                        placeholder={enumStrings.createMemberForm.placeholders.enterContactTitle}
                                        className={classes.companyInput}
                                        variant="outlined"
                                        inputProps={{ autoComplete: "new-password" }}
                                        name="cpaAdminCompanyAdminContactTitle"
                                        value={formik.values.cpaAdminCompanyAdminContactTitle}
                                        onFocus={() => { adminFieldsTouched.current.cpaAdminCompanyAdminContactTitle = true; }}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                            </div>

                            <div className={classes.formRow}>
                                <div>
                                    <div className={classes.fieldLabel}>
                                        <span>{enumStrings.createMemberForm.contactEmail}<span className={classes.requiredAsterisk}>*</span></span>
                                    </div>
                                    <div style={{ fontSize: "12px", color: "#666666", marginBottom: "8px" }}>
                                        {enumStrings.createMemberForm.adminEmailNote}
                                    </div>
                                    <TextField
                                        placeholder={enumStrings.createMemberForm.placeholders.enterContactEmail}
                                        className={classes.companyInput}
                                        variant="outlined"
                                        inputProps={{ autoComplete: "new-password" }}
                                        name="adminConsoleLoginEmail"
                                        value={formik.values.adminConsoleLoginEmail}
                                        onFocus={() => { adminFieldsTouched.current.adminConsoleLoginEmail = true; }}
                                        onChange={formik.handleChange}
                                        error={formik.touched.adminConsoleLoginEmail && Boolean(formik.errors.adminConsoleLoginEmail)}
                                        helperText={formik.touched.adminConsoleLoginEmail && formik.errors.adminConsoleLoginEmail}
                                    />
                                </div>
                                <div>
                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <div className={classes.fieldLabel} style={{ flex: 1 }}>
                                            <span>{enumStrings.createMemberForm.contactPhone}</span>
                                        </div>
                                        <div className={classes.fieldLabel} style={{ width: "100px" }}>
                                            {enumStrings.createMemberForm.extensionNo}
                                        </div>
                                    </div>
                                    <div style={{ fontSize: "14px", marginBottom: "8px", lineHeight: "24px", visibility: "hidden" as const }}>&nbsp;</div>
                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <TextField
                                            placeholder={enumStrings.createMemberForm.placeholders.enterContactPhone}
                                            className={classes.companyInput}
                                            variant="outlined"
                                            inputProps={{ autoComplete: "new-password" }}
                                            name="cpaAdminCompanyAdminContactNumber"
                                            value={formik.values.cpaAdminCompanyAdminContactNumber}
                                            onFocus={() => { adminFieldsTouched.current.cpaAdminCompanyAdminContactNumber = true; }}
                                            onChange={formik.handleChange}
                                            error={formik.touched.cpaAdminCompanyAdminContactNumber && Boolean(formik.errors.cpaAdminCompanyAdminContactNumber)}
                                            helperText={formik.touched.cpaAdminCompanyAdminContactNumber && formik.errors.cpaAdminCompanyAdminContactNumber}
                                            InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                                            style={{ flex: 1 }}
                                        />
                                        <TextField
                                            placeholder={enumStrings.createMemberForm.extensionPlaceholder}
                                            className={classes.companyInput}
                                            variant="outlined"
                                            inputProps={{ autoComplete: "new-password" }}
                                            name="cpaAdminCompanyAdminContactNumberExtension"
                                            value={formik.values.cpaAdminCompanyAdminContactNumberExtension}
                                            onFocus={() => { adminFieldsTouched.current.cpaAdminCompanyAdminContactNumberExtension = true; }}
                                            onChange={formik.handleChange}
                                            style={{ width: "100px" }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={classes.importantWarning}>
                                <div className={classes.importantDesc} dangerouslySetInnerHTML={{ __html: enumStrings.createMemberForm.adminContactImportantDesc }} />
                            </div>
                        </div>

                        {/* Contact for Employee Adoption Section */}
                        <div className={classes.formSection}>
                            <div className={classes.sectionHeader}>
                                <p className={classes.sectionTitle}>{enumStrings.createMemberForm.employeeAdoptionContact}</p>
                            </div>
                            <p className={classes.sectionHelperText}>
                                {enumStrings.createMemberForm.employeeAdoptionDescMember}
                            </p>
                            <p className={classes.sectionHelperText} style={{ fontWeight: 700 }}>
                                {enumStrings.createMemberForm.employeeAdoptionNotAppear}
                            </p>

                            <div className={classes.formRow}>
                                <div>
                                    <div className={classes.fieldLabel}>{enumStrings.createMemberForm.employeeContactName}</div>
                                    <TextField
                                        placeholder={enumStrings.createMemberForm.placeholders.enterEmployeeContactName}
                                        className={classes.companyInput}
                                        variant="outlined"
                                        inputProps={{ autoComplete: "new-password" }}
                                        name="cpaEmployeeAdoptionContactName"
                                        value={formik.values.cpaEmployeeAdoptionContactName}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                                <div>
                                    <div className={classes.fieldLabel}>{enumStrings.createMemberForm.title}</div>
                                    <TextField
                                        placeholder={enumStrings.createMemberForm.placeholders.enterContactTitle}
                                        className={classes.companyInput}
                                        variant="outlined"
                                        inputProps={{ autoComplete: "new-password" }}
                                        name="cpaEmployeeAdoptionContactTitle"
                                        value={formik.values.cpaEmployeeAdoptionContactTitle}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                            </div>

                            <div className={classes.formRow}>
                                <div>
                                    <div className={classes.fieldLabel}>{enumStrings.createMemberForm.employeeContactEmail}</div>
                                    <TextField
                                        placeholder={enumStrings.createMemberForm.placeholders.enterEmployeeContactEmail}
                                        className={classes.companyInput}
                                        variant="outlined"
                                        inputProps={{ autoComplete: "new-password" }}
                                        name="cpaEmployeeAdoptionContactEmail"
                                        value={formik.values.cpaEmployeeAdoptionContactEmail}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                                <div>
                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <div className={classes.fieldLabel} style={{ flex: 1 }}>
                                            {enumStrings.createMemberForm.employeeContactPhone}
                                        </div>
                                        <div className={classes.fieldLabel} style={{ width: "100px" }}>
                                            {enumStrings.createMemberForm.extensionNo}
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <TextField
                                            placeholder={enumStrings.createMemberForm.placeholders.enterEmployeeContactPhone}
                                            className={classes.companyInput}
                                            variant="outlined"
                                            inputProps={{ autoComplete: "new-password" }}
                                            name="cpaEmployeeAdoptionContactNumber"
                                            value={formik.values.cpaEmployeeAdoptionContactNumber}
                                            onChange={formik.handleChange}
                                            InputProps={{ inputComponent: PhoneNumberFormatCustom as any }}
                                            style={{ flex: 1 }}
                                        />
                                        <TextField
                                            placeholder={enumStrings.createMemberForm.extensionPlaceholder}
                                            className={classes.companyInput}
                                            variant="outlined"
                                            inputProps={{ autoComplete: "new-password" }}
                                            name="cpaEmployeeAdoptionContactNumberExtension"
                                            value={formik.values.cpaEmployeeAdoptionContactNumberExtension}
                                            onChange={formik.handleChange}
                                            style={{ width: "100px" }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>)}

                    {showCmpContactCards && (
                        <CmpCompanyContactCards
                            formik={formik}
                            classes={classes}
                            chamberName={chamberInfo?.name || "your chamber"}
                            timeZone={memberUser?.chamberMemberProTimeZone}
                            savedRevision={contactsSavedAt}
                        />
                    )}

                    {/* Featured Member Profile Banners Section */}
                    <div className={classes.formSection}>
                        <div className={isPayerActive ? classes.sectionHeaderGreen : classes.sectionHeaderBlack}>
                            <p className={classes.sectionTitle}>
                                {isPayerActive
                                    ? enumStrings.editMemberForm.featuredMemberActivatedTitle
                                    : enumStrings.editMemberForm.featuredMemberNotActivatedTitle}
                            </p>
                        </div>

                        {isPayerActive ? (
                            <p className={classes.bannerInfoText} dangerouslySetInnerHTML={{ __html: enumStrings.editMemberForm.featuredMemberUpgradedDesc }} />
                        ) : (
                            <p className={classes.bannerInfoTextLarge}>
                                {enumStrings.createMemberForm.featuredMemberNotUpgraded
                                    .split("{notUpgraded}")[0]}
                                <span className={classes.boldText}>{enumStrings.createMemberForm.notUpgradedBold}</span>
                                {enumStrings.createMemberForm.featuredMemberNotUpgraded
                                    .split("{notUpgraded}")[1]
                                    ?.split("{clickHere}")[0]}
                                <span
                                    className={classes.clickHereLink}
                                    onClick={() => setContactChamberOpen(true)}
                                >
                                    {enumStrings.createMemberForm.clickHereLink}
                                </span>
                                {enumStrings.createMemberForm.featuredMemberNotUpgraded
                                    .split("{clickHere}")[1]}
                            </p>
                        )}

                        <FeaturedMemberBannerGuidance className={classes.bannerRecommendationText} />

                        <div className={classes.bannersGrid}>
                            {isPayerActive ? (
                                bannersData.map((banner, index) => (
                                    <div key={index} className={classes.bannerContainer}>
                                        <div className={classes.bannerLabel}>
                                            {enumStrings.editMemberForm.bannerLabel} {index + 1}
                                        </div>
                                        {banner.image || banner.videoUrl ? (
                                            <div className={classes.bannerCard}>
                                                <div className={classes.bannerImageContainer}>
                                                    {banner.image ? (
                                                        <img
                                                            src={banner.image}
                                                            alt={`Banner ${index + 1}`}
                                                            className={classes.bannerImage}
                                                        />
                                                    ) : (
                                                        <div className={classes.videoPlaceholder}>
                                                            <img src={PlayVideoIcon} alt="Play" className={classes.iconSize36} />
                                                            <span className={classes.videoWatchText}>{enumStrings.editMemberForm.watchVideo}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className={classes.bannerActions}>
                                                    <div className={classes.editDeleteButtons}>
                                                        <IconButton
                                                            size="small"
                                                            className={classes.editButton}
                                                            onClick={() => handleOpenBannerModal(index + 1)}
                                                        >
                                                            <img src={EditOrangeIcon} alt="Edit" className={classes.iconSize20} />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            className={classes.trashButton}
                                                            onClick={() => handleDeleteBanner(index)}
                                                        >
                                                            <img src={TrashIcon} alt="Delete" className={`${classes.iconSize20} ${classes.iconInverted}`} style={{ height: "22px" }} />
                                                        </IconButton>
                                                    </div>
                                                    <div className={classes.arrowButtons}>
                                                        <IconButton
                                                            size="small"
                                                            className={classes.arrowButton}
                                                            onClick={() => handleMoveBanner(index, 'left')}
                                                            disabled={index === 0}
                                                        >
                                                            <ArrowBackIosIcon className={classes.arrowIconLeft} />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            className={classes.arrowButton}
                                                            onClick={() => handleMoveBanner(index, 'right')}
                                                            disabled={index === bannersData.length - 1}
                                                        >
                                                            <ArrowForwardIosIcon className={classes.arrowIconSmall} />
                                                        </IconButton>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                onClick={() => handleOpenBannerModal(index + 1)}
                                                className={classes.bannerCardEmpty}
                                            >
                                                <img src={UploadRoundIcon} alt="Upload" className={classes.iconSize36} />
                                                <p className={classes.uploadBannerText}>
                                                    {enumStrings.editMemberForm.uploadBanner}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                [1, 2, 3, 4].map((num) => (
                                    <div key={num}>
                                        <div className={classes.bannerSlotDisabled}>
                                            <img src={UploadRoundIcon} alt="Upload" className={classes.bannerSlotDisabledIcon} />
                                            <span className={classes.bannerSlotDisabledText}>{enumStrings.editMemberForm.uploadBanner}</span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <p className={classes.bannerRecommendationText} style={{ marginTop: "24px", marginBottom: "24px" }} dangerouslySetInnerHTML={{ __html: enumStrings.editMemberForm.bannerPreviewFooter }} />

                        <div className={classes.centerFlexContainer}>
                            <Button
                                className={classes.previewBannersButton}
                                onClick={handlePreviewBanners}
                                disabled={!isPayerActive}
                                style={!isPayerActive ? { cursor: "not-allowed" } : {}}
                            >
                                {enumStrings.createMemberForm.previewBannersButton}
                            </Button>
                        </div>
                    </div>

                    <div style={{ textAlign: "center", marginTop: "40px" }}>
                        <Button type="submit" className={classes.updateButton} disabled={loader || buttonDisabled}>
                            {loader ? <CircularProgress size={24} style={{ color: "white" }} /> : enumStrings.editMemberForm.updateMemberButton}
                        </Button>
                    </div>
                </form>
            </div>

            {/* Banner Upload Modal */}
            <BannerUploadModal
                open={bannerModalOpen}
                onClose={() => setBannerModalOpen(false)}
                bannerIndex={currentBannerIndex}
                useFeaturedMemberGuidance
                initialData={bannersData[currentBannerIndex - 1] || {}}
                onSave={handleSaveBanner}
            />

            {/* Delete Banner Confirmation Modal */}
            <DeleteBannerModal
                open={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setPendingDeleteIndex(-1);
                }}
                onDelete={confirmDeleteBanner}
            />

            {/* Banner Preview Modal */}
            <BannerPreviewModal
                open={previewModalOpen}
                onClose={() => setPreviewModalOpen(false)}
                banners={previewBannersList}
            />

            {/* Contact Chamber Dialog */}
            <ActionDialog
                open={contactChamberOpen}
                onClose={() => setContactChamberOpen(false)}
                icon={<MailOutlineIcon style={{ color: "#FFFFFF", fontSize: "40px" }} />}
                iconBgColor="#4CAF50"
                title={enumStrings.createMemberForm.contactChamberTitle}
                description={enumStrings.createMemberForm.contactChamberDesc}
                buttonText={enumStrings.createMemberForm.contactChamberSendRequest}
                onButtonClick={() => {
                    const memberName = memberData?.getChamberMember?.name || "";
                    const chamberName = chamberInfo?.name || "";
                    const toEmail = chamberInfo?.supportContactEmail || "";
                    const subject = encodeURIComponent(
                        enumStrings.createMemberForm.contactChamberEmailSubject.replace("{memberName}", memberName)
                    );
                    const body = encodeURIComponent(
                        enumStrings.createMemberForm.contactChamberEmailBody
                            .replace("{chamberName}", chamberName)
                            .replace("{memberName}", memberName)
                    );
                    window.open(`mailto:${toEmail}?subject=${subject}&body=${body}`, "_blank");
                    setContactChamberOpen(false);
                }}
            />

            {/* IDA Generate Description Dialog */}
            <IDAGenerateModal
                open={idaDialogOpen}
                onClose={() => setIdaDialogOpen(false)}
                keyDetails={idaKeyDetails}
                onKeyDetailsChange={setIdaKeyDetails}
                entityName={formik.values.name || "the company"}
                entityType="company"
                onGenerate={(generated) => {
                    setIdaGeneratedText(generated);
                    setIdaDialogOpen(false);
                    setOverwriteDialogOpen(true);
                }}
            />

            {/* IDA Overwrite Confirmation Dialog */}
            <IDAOverwriteDialog
                open={overwriteDialogOpen}
                onClose={() => setOverwriteDialogOpen(false)}
                onRegenerate={() => {
                    setOverwriteDialogOpen(false);
                    setIdaDialogOpen(true);
                }}
                onConfirm={() => {
                    formik.setFieldValue("description", idaGeneratedText);
                    setOverwriteDialogOpen(false);
                    setDescriptionGeneratedByIda(true);
                    setIdaSuccessDialogOpen(true);
                    setTimeout(() => setIdaSuccessDialogOpen(false), 5000);
                }}
                title={enumStrings.idaGenerate.overwriteTitle}
                description={enumStrings.idaGenerate.overwriteDesc}
                generatedLabel={enumStrings.idaGenerate.generatedLabel}
                generatedText={idaGeneratedText}
            />

            {/* IDA Success Dialog */}
            <SuccessDialog
                open={idaSuccessDialogOpen}
                onClose={() => setIdaSuccessDialogOpen(false)}
                message={enumStrings.toastMessages.idaGeneratedSuccess}
            />

            {/* Update Confirmation Dialog */}
            <ActionDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                icon={
                    <img
                        src={MingcuteQuestionIcon}
                        alt="Question"
                        style={{ width: "60px", height: "60px" }}
                    />
                }
                iconBgColor="#E1A731"
                iconSize={90}
                description={
                    <div>
                        <p
                            style={{
                                fontWeight: 600,
                                fontSize: "18px",
                                lineHeight: "200%",
                                textAlign: "center",
                                color: "#000000",
                                margin: 0,
                            }}
                        >
                            {enumStrings.manageCompanyProfile.confirmUpdateTitle}
                        </p>
                        <p
                            style={{
                                fontWeight: 400,
                                fontSize: "18px",
                                lineHeight: "200%",
                                textAlign: "center",
                                color: "#000000",
                                margin: 0,
                            }}
                        >
                            {enumStrings.manageCompanyProfile.confirmUpdateDescription}
                        </p>
                    </div>
                }
                buttonText={enumStrings.manageCompanyProfile.iAcceptButton}
                onButtonClick={handleConfirmUpdate}
                maxWidth="541px"
            />

            {/* Update Success Dialog - simple (no admin contact change) */}
            <SuccessDialog
                open={updateSuccessDialogOpen && !adminContactChanged}
                onClose={() => setUpdateSuccessDialogOpen(false)}
                message={enumStrings.manageCompanyProfile.updateSuccessTitle}
            />

            {/* Update Success Dialog - full (admin contact changed) - logs out user */}
            <ActionDialog
                open={updateSuccessDialogOpen && adminContactChanged}
                onClose={() => {
                    setUpdateSuccessDialogOpen(false);
                    handleLogout();
                }}
                iconVariant="success"
                description={
                    <div>
                        <p
                            style={{
                                fontWeight: 600,
                                fontSize: "18px",
                                lineHeight: "200%",
                                textAlign: "center",
                                color: "#000000",
                                margin: 0,
                            }}
                        >
                            {enumStrings.manageCompanyProfile.updateSuccessTitle}
                        </p>
                        <p
                            style={{
                                fontWeight: 400,
                                fontSize: "18px",
                                lineHeight: "200%",
                                textAlign: "center",
                                color: "#000000",
                                margin: 0,
                            }}
                        >
                            {enumStrings.manageCompanyProfile.credentialsUpdatedLogout}
                        </p>
                        <p
                            style={{
                                fontWeight: 700,
                                fontSize: "18px",
                                lineHeight: "200%",
                                textAlign: "center",
                                color: "#000000",
                                margin: 0,
                            }}
                        >
                            {enumStrings.manageCompanyProfile.checkSpamFolder}
                        </p>
                    </div>
                }
                maxWidth="541px"
                autoDismissMs={10000}
            />
        </div>
    );
};

export default ManageCompanyProfile;
